import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import { CircleAlert, Calendar as CalendarIcon } from 'lucide-react';
import { C, G } from './uiTokens';

interface GlobalDateFieldProps {
  label?: string;
  value: Nullable<Date | Date[] | (Date | null)[]>;
  onChange: (value: Nullable<Date | Date[] | (Date | null)[]>) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /** red border without a message (e.g. when the error is shown elsewhere) */
  invalid?: boolean;
  /** content aligned to the right of the label */
  labelRight?: React.ReactNode;
  fullWidth?: boolean;
  showTime?: boolean;
  timeOnly?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  showButtonBar?: boolean;
  selectionMode?: 'single' | 'multiple' | 'range';
  inline?: boolean;
  monthNavigator?: boolean;
  yearNavigator?: boolean;
  yearRange?: string;
  name?: string;
  id?: string;
  /** hide the trailing calendar icon */
  hideIcon?: boolean;
}

/** Strip time so a fresh `new Date()` prop doesn't change identity on every render. */
const startOfDay = (d?: Date) =>
  d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : undefined;

export const GlobalDateField: React.FC<GlobalDateFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'dd/mm/yyyy',
  required = false,
  disabled = false,
  error = '',
  hint,
  invalid = false,
  labelRight,
  fullWidth = true,
  showTime = false,
  timeOnly = false,
  dateFormat = 'dd/mm/yy',
  minDate,
  maxDate,
  showButtonBar = false,
  selectionMode = 'single',
  inline = false,
  monthNavigator = false,
  yearNavigator = false,
  yearRange = '2000:2030',
  name,
  id,
  hideIcon = false,
}) => {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Esc. The panel is portaled to document.body, so it is
  // checked separately from the wrapper.
  useEffect(() => {
    if (!open || inline) return;

    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (wrapperRef.current?.contains(t)) return;
      if (t.closest('.global-date-panel')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, inline]);

  const hasError = !!error || invalid;
  const borderColor = hasError ? C.danger : focused ? 'rgba(17,85,80,0.5)' : G.line;
  const hoverBorder = hasError ? C.danger : 'rgba(17,85,80,0.3)';
  const focusRing = hasError ? 'rgba(220,38,38,0.1)' : 'rgba(17,85,80,0.08)';

  const stableMin = useMemo(
    () => startOfDay(minDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [minDate?.getFullYear(), minDate?.getMonth(), minDate?.getDate()]
  );
  const stableMax = useMemo(
    () => startOfDay(maxDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxDate?.getFullYear(), maxDate?.getMonth(), maxDate?.getDate()]
  );

  const handleChange = (e: { value: Nullable<Date | Date[] | (Date | null)[]> }) => {
    onChange(e.value);
  };

  // Only ever opens (never toggles). Closing is handled by Calendar via onVisibleChange
  // (date select, outside click, Esc, Tab), so there is a single source of truth.
  // The calendar icon button sits inside this wrapper, so its click bubbles here too.
  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || inline || open) return;
    // the icon button opens the panel itself (deferred), so skip it here
    if ((e.target as HTMLElement).closest('button[aria-label="Open calendar"]')) return;
    setOpen(true);
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: '5.9px',
          }}
        >
          <label
            htmlFor={id}
            style={{
              display: 'block',
              fontSize: '0.73rem',
              fontWeight: 600,
              color: hasError ? C.danger : '#374151',
            }}
          >
            {label}
            {required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
          </label>
          {labelRight}
        </div>
      )}

      <div
        ref={wrapperRef}
        style={
          {
            position: 'relative',
            // per-instance values via CSS vars so multiple fields don't clobber each other
            '--gdf-border': borderColor,
            '--gdf-hover-border': hoverBorder,
            '--gdf-ring': focusRing,
            '--gdf-shadow': focused ? `0 0 0 3px ${focusRing}` : 'none',
            '--gdf-bg': disabled ? '#f9fafb' : 'white',
            '--gdf-color': disabled ? C.muted : '#1f2937',
            '--gdf-pad-right': hideIcon || inline ? '11.8px' : '38px',
            '--gdf-cursor': disabled ? 'not-allowed' : 'text',
          } as React.CSSProperties
        }
        onClick={handleWrapperClick}
      >
        <Calendar
          inputId={id}
          name={name}
          value={value}
          onChange={handleChange}
          visible={inline ? undefined : open}
          onVisibleChange={(e) => setOpen(!!e.visible)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          disabled={disabled}
          showTime={showTime}
          timeOnly={timeOnly}
          dateFormat={dateFormat}
          minDate={stableMin}
          maxDate={stableMax}
          showButtonBar={showButtonBar}
          showIcon={false}
          selectionMode={selectionMode}
          inline={inline}
          monthNavigator={monthNavigator}
          yearNavigator={yearNavigator}
          yearRange={yearRange}
          style={{ width: '100%' }}
          appendTo={inline ? undefined : document.body}
          panelClassName="global-date-panel"
          className="global-date-field"
          inputClassName="global-date-input"
        />

        {!hideIcon && !inline && (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Open calendar"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              // Open on the next tick so this same click can't hit PrimeReact's own
              // outside-click listener and close the panel instantly. Click again = close.
              // No stopPropagation, so other open calendars still get closed.
              if (open) setOpen(false);
              else setTimeout(() => setOpen(true), 0);
            }}
            style={{
              position: 'absolute',
              right: '6px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              padding: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: open || focused ? C.teal : C.muted,
              transition: 'color 0.18s',
              zIndex: 1,
            }}
          >
            <CalendarIcon size={15} />
          </button>
        )}
      </div>

      {error ? (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4.3px',
            marginTop: '5.4px',
            fontSize: '0.71rem',
            color: C.danger,
            fontWeight: 500,
          }}
        >
          <CircleAlert size={12} /> {error}
        </span>
      ) : hint ? (
        <span
          style={{
            display: 'block',
            marginTop: '5.4px',
            fontSize: '0.71rem',
            color: C.sub,
          }}
        >
          {hint}
        </span>
      ) : null}

      {/* Static CSS: identical for every instance (dynamic values come from CSS vars above) */}
      <style>{`
        .global-date-field.p-calendar,
        .global-date-field .p-calendar {
          width: 100%;
        }

        .global-date-field .p-inputtext,
        .global-date-input {
          width: 100% !important;
          box-sizing: border-box !important;
          height: 38.7px !important;
          padding: 0 var(--gdf-pad-right, 38px) 0 11.8px !important;
          border: 1px solid var(--gdf-border, ${G.line}) !important;
          border-radius: 8.9px !important;
          font-size: 0.83rem !important;
          font-weight: 500 !important;
          font-family: inherit !important;
          background: var(--gdf-bg, white) !important;
          color: var(--gdf-color, #1f2937) !important;
          outline: none !important;
          box-shadow: var(--gdf-shadow, none) !important;
          transition: border-color 0.18s, box-shadow 0.18s !important;
          cursor: var(--gdf-cursor, text) !important;
        }

        .global-date-field .p-inputtext::placeholder,
        .global-date-input::placeholder {
          color: rgba(17,85,80,0.3) !important;
          opacity: 1;
        }

        .global-date-field .p-inputtext:enabled:hover,
        .global-date-input:enabled:hover {
          border-color: var(--gdf-hover-border, rgba(17,85,80,0.3)) !important;
        }

        .global-date-field .p-inputtext:enabled:focus,
        .global-date-input:enabled:focus {
          border-color: var(--gdf-border, ${G.line}) !important;
          box-shadow: 0 0 0 3px var(--gdf-ring, rgba(17,85,80,0.08)) !important;
        }

        /* ── Panel (portaled to document.body, so styled as a top-level class) ── */
        .global-date-panel.p-datepicker {
          background: white !important;
          border: 1px solid ${G.line} !important;
          border-radius: 12px !important;
          box-shadow: 0 10px 40px rgba(17,85,80,0.15) !important;
          padding: 12px;
          font-family: inherit;
          z-index: 9999 !important;
          min-width: 300px;
        }

        /* A closing panel must never intercept clicks meant for the next field */
        .global-date-panel.p-datepicker.p-connected-overlay-exit,
        .global-date-panel.p-datepicker.p-connected-overlay-exit-active {
          pointer-events: none !important;
        }

        .global-date-panel .p-datepicker-header {
          padding: 10px 12px;
          background: linear-gradient(135deg, rgba(17,85,80,0.04) 0%, rgba(17,85,80,0.02) 100%);
          border-radius: 8px 8px 0 0;
          border-bottom: 1px solid ${G.line};
        }

        .global-date-panel .p-datepicker-header .p-datepicker-title {
          margin: 0;
          color: ${C.teal};
          font-weight: 700;
          font-size: 0.85rem;
        }

        .global-date-panel .p-datepicker-header .p-datepicker-prev,
        .global-date-panel .p-datepicker-header .p-datepicker-next {
          color: ${C.teal};
          opacity: 0.55;
          width: 30px;
          height: 30px;
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-datepicker-header .p-datepicker-prev:hover,
        .global-date-panel .p-datepicker-header .p-datepicker-next:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
          opacity: 1;
        }

        .global-date-panel table td {
          padding: 3px;
        }

        .global-date-panel table td > span {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          transition: all 0.15s ease;
          color: #333;
          font-size: 0.81rem;
          font-weight: 500;
        }

        .global-date-panel table td > span:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-panel table td > span.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          font-weight: 700;
          box-shadow: 0 3px 10px rgba(17,85,80,0.28);
        }

        .global-date-panel table td.p-datepicker-today > span {
          background: rgba(200,233,75,0.22);
          color: ${C.teal};
          font-weight: 700;
          border: 1px solid rgba(200,233,75,0.5);
        }

        .global-date-panel table td.p-datepicker-today > span.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          border: none;
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month,
        .global-date-panel .p-yearpicker .p-yearpicker-year {
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month:hover,
        .global-date-panel .p-yearpicker .p-yearpicker-year:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-panel .p-monthpicker .p-monthpicker-month.p-highlight,
        .global-date-panel .p-yearpicker .p-yearpicker-year.p-highlight {
          background: linear-gradient(135deg, ${C.teal} 0%, ${C.tealMid ?? C.teal} 100%);
          color: white;
          font-weight: 700;
        }

        .global-date-panel .p-timepicker {
          border-top: 1px solid ${G.line};
          padding: 10px 12px;
          margin-top: 6px;
        }

        .global-date-panel .p-timepicker button {
          color: ${C.teal};
          opacity: 0.55;
          width: 30px;
          height: 30px;
          border-radius: 8px;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-timepicker button:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
          opacity: 1;
        }

        .global-date-panel .p-datepicker-buttonbar {
          padding: 10px 12px;
          border-top: 1px solid ${G.line};
          background: linear-gradient(135deg, rgba(17,85,80,0.04) 0%, rgba(17,85,80,0.02) 100%);
          border-radius: 0 0 8px 8px;
        }

        .global-date-panel .p-datepicker-buttonbar button {
          padding: 7px 14px;
          border-radius: 8px;
          font-size: 0.81rem;
          font-weight: 600;
          transition: all 0.15s ease;
        }

        .global-date-panel .p-datepicker-buttonbar .p-button-text {
          color: ${C.sub};
        }

        .global-date-panel .p-datepicker-buttonbar .p-button-text:hover {
          background: rgba(17,85,80,0.08);
          color: ${C.teal};
        }

        .global-date-field.p-calendar-disabled .p-inputtext,
        .global-date-field .p-calendar.p-calendar-disabled .p-inputtext {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Inline mode still lives inside the wrapper, so it keeps the nested selector */
        .global-date-field .p-datepicker-inline {
          border: 1px solid ${G.line};
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(17,85,80,0.08);
        }

        .global-date-panel table th.p-datepicker-weeknumber,
        .global-date-panel table td.p-datepicker-weeknumber {
          color: ${C.muted};
          font-weight: 500;
          font-size: 0.72rem;
        }

        .global-date-panel table td.p-disabled > span {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .global-date-panel table td.p-datepicker-other-month > span {
          color: #ccc;
        }
      `}</style>
    </div>
  );
};